// ./src/components/features/CreateEventWizard/steps/TasksStep.tsx
//
// "What needs doing?" — shown when the Tasks feature is on (ROADMAP A2b #62 P6(e)). Tasks are made in the Tasks page's
// own `TaskEditDrawer` under a provider scoped by the event; a one-tap starter only opens it with a title filled in
// (`wizardStarters.ts`).
//
// ⚠ The drawer edits a task's date in the EVENT's timezone, so the step reads the event first and the list (and Add)
//   wait for that read — a task scheduled in the browser's zone would land hours off for a host abroad.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Stack, Text } from '@mantine/core';
import { OpResult } from '@sdflc/api-helpers';
import { useTranslation } from 'react-i18next';

import { TasksProvider, useTasksContext } from '@/contexts';
import { useEvent, useToast } from '@/hooks';
import { Task, WHOLE_SET } from '@/utils/api';
import { ConfirmModal } from '@/components/generic';
import { TaskEditDrawer, formatScheduledAt } from '@/components/features/Tasks';
import { refusalReason } from '@/utils';

import { useEventWizard } from '../WizardContext';
import { WizardItemRow } from '../components/WizardItemRow';
import { WizardListSection } from '../components/WizardListSection';
import { TASK_STARTERS, startersFor, unusedStarters } from '../wizardStarters';
import { useTypeIdeas } from '../useTypeIdeas';

/** Soonest first; a task with no date after every dated one. */
const byTimeNullsLast = (a?: string | null, b?: string | null): number => {
  if (a && b) {
    return a.localeCompare(b);
  }

  return a ? -1 : b ? 1 : 0;
};

const TasksStepBody: React.FC<{ eventId: string }> = ({ eventId }) => {
  const { t } = useTranslation(['events', 'common']);
  const toast = useToast();
  const { saving } = useEventWizard();
  const { tasks, list, isLoaded, remove } = useTasksContext();
  const { event, error: eventError, reload: reloadEvent } = useEvent(eventId);

  const [readError, setReadError] = useState<OpResult | null>(null);
  const [drawer, setDrawer] = useState<{ recordId: string | null; title?: string } | null>(null);
  const [pendingRemove, setPendingRemove] = useState<Task | null>(null);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    const res = await list({ eventId: [eventId] }, { page: 1, pageSize: WHOLE_SET }, undefined, true);

    setReadError(res.didSucceed() ? null : res);
  }, [list, eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  const sorted = useMemo(() => [...tasks].sort((a, b) => byTimeNullsLast(a.scheduledAt, b.scheduledAt)), [tasks]);
  // The label is both the button and the saved name, so it is translated before anything compares it.
  // ⓘ The TYPE's own ideas when it has any (#62 P6(h)), else the generic ones.
  const typeIdeas = useTypeIdeas('tasks');
  const starters = unusedStarters(
    startersFor(typeIdeas, TASK_STARTERS.map((s) => ({ ...s, label: t(`events:wizard.tasks.starter.${s.key}`, { defaultValue: s.label }) })), 'Checklist'),
    tasks.map((task) => task.title),
  );

  const afterSave = () => {
    setDrawer(null);
    void load();
  };

  const confirmRemove = async () => {
    if (!pendingRemove?.id) {
      return;
    }

    setRemoving(true);

    const res = await remove(pendingRemove.id);

    setRemoving(false);

    if (!res.didSucceed()) {
      toast.error(refusalReason(res));

      return;
    }

    setPendingRemove(null);
    void load();
  };

  return (
    <Stack gap="lg">
      <Text size="md" c="dimmed">
        {t('events:wizard.tasks.intro', {
          defaultValue:
            'List what has to be done before the day, and who does it. You and your crew tick tasks off on the event’s Tasks page. Guests see them only if you put a Tasks block on your event page.',
        })}
      </Text>

      <WizardListSection
        title={t('events:wizard.tasks.title', { defaultValue: 'Tasks' })}
        addLabel={t('events:wizard.tasks.add', { defaultValue: 'Add a task' })}
        onAdd={() => setDrawer({ recordId: null })}
        gate={null}
        gateWhat=""
        disabled={saving || !event}
        starters={starters.map((s) => ({ key: s.key, label: s.label, icon: s.icon, onClick: () => setDrawer({ recordId: null, title: s.label }) }))}
        isLoaded={(isLoaded || !!readError) && (!!event || !!eventError)}
        readError={readError ?? eventError}
        onRetry={() => {
          void reloadEvent();
          void load();
        }}
        errorTitle={t('events:wizard.tasks.loadFailed', { defaultValue: 'Could not load the tasks' })}
        operation="taskList"
        emptyText={t('events:wizard.tasks.none', { defaultValue: 'No tasks yet.' })}
        isEmpty={sorted.length === 0}
      >
        {sorted.map((task) => (
          <WizardItemRow
            key={task.id}
            icon="Checklist"
            title={task.title ?? ''}
            subtitle={formatScheduledAt(task.scheduledAt, event?.timezone) ?? t('events:wizard.tasks.noDate', { defaultValue: 'No date yet' })}
            disabled={saving}
            onEdit={() => setDrawer({ recordId: task.id as string })}
            onRemove={() => setPendingRemove(task)}
          />
        ))}
      </WizardListSection>

      <TaskEditDrawer
        opened={!!drawer}
        onClose={() => setDrawer(null)}
        recordId={drawer?.recordId ?? null}
        eventId={eventId}
        eventTimezone={event?.timezone ?? null}
        defaultTitle={drawer?.title ?? null}
        onCreated={afterSave}
        onUpdated={afterSave}
      />

      <ConfirmModal
        opened={!!pendingRemove}
        onClose={() => setPendingRemove(null)}
        onConfirm={() => void confirmRemove()}
        type="danger"
        confirmInProgress={removing}
        title={t('events:wizard.tasks.removeTitle', { defaultValue: 'Remove this task?' })}
        message={t('events:wizard.tickets.removeMessage', { defaultValue: '“{{name}}” will be removed from this event.', name: pendingRemove?.title ?? '' })}
        confirmLabel={t('common:actions.remove', { defaultValue: 'Remove' })}
      />
    </Stack>
  );
};

export const TasksStep: React.FC = () => {
  const { t } = useTranslation(['events']);
  const { createdEventId } = useEventWizard();

  if (!createdEventId) {
    return (
      <Text size="md" c="dimmed">
        {t('events:wizard.tickets.noEvent', { defaultValue: 'Your event is created at the end of the Guests step.' })}
      </Text>
    );
  }

  return (
    <TasksProvider initialFilters={{ eventId: [createdEventId] }} pageSize={WHOLE_SET} autoLoad={false}>
      <TasksStepBody eventId={createdEventId} />
    </TasksProvider>
  );
};
